import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { metricsAPI } from '../../services/metricsApi';
import { curriculumAPI } from '../../services/api';
import CoachLayout from './CoachLayout';

// ── Stat Card ────────────────────────────────────────────────────────────
function StatCard({ label, value, color = '#4f46e5', sub }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6', textAlign: 'center', minWidth: 140 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Gauge ────────────────────────────────────────────────────────────────
function Gauge({ label, value, max = 100, color = '#4f46e5' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
        <span style={{ color, fontWeight: 600 }}>{typeof value === 'number' ? (max === 1 ? `${Math.round(value * 100)}%` : `${value}%`) : value}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: '#f3f4f6', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: color, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

// ── Funnel ───────────────────────────────────────────────────────────────
function CapstoneFunnel({ summary }) {
  const stages = [
    { key: 'not_started', label: 'Not Started', color: '#e5e7eb' },
    { key: 'submitted', label: 'Submitted', color: '#93c5fd' },
    { key: 'graded', label: 'Graded', color: '#fbbf24' },
    { key: 'passed', label: 'Passed', color: '#34d399' },
  ];
  const total = Object.values(summary || {}).reduce((a, b) => a + b, 0) || 1;
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 100 }}>
      {stages.map(s => {
        const count = (summary || {})[s.key] || 0;
        const h = Math.max((count / total) * 100, 8);
        return (
          <div key={s.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{count}</div>
            <div style={{ width: '100%', height: h, background: s.color, borderRadius: 4, transition: 'height 0.4s ease' }} />
            <div style={{ fontSize: 10, color: '#6b7280', textAlign: 'center' }}>{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────
export default function CoachAnalyticsPage() {
  const { token } = useAuth();
  const [domain, setDomain] = useState('');
  const [domains, setDomains] = useState([]);
  const [data, setData] = useState(null);
  const [effectiveness, setEffectiveness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('completion_pct');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    curriculumAPI.listDomains()
      .then(({ data: resp }) => {
        const list = Array.isArray(resp?.domains) ? resp.domains : [];
        setDomains(list);
      })
      .catch(() => setDomains([]));
  }, []);

  const fetchData = useCallback(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      metricsAPI.getCoachDashboard(token, domain),
      metricsAPI.getContentEffectiveness(token, domain),
    ]).then(([dash, eff]) => {
      setData(dash);
      setEffectiveness(eff);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [token, domain]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleSort(key) {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  }

  const sortedLeaderboard = React.useMemo(() => {
    if (!data?.learner_leaderboard) return [];
    return [...data.learner_leaderboard].sort((a, b) => {
      const diff = (a[sortKey] || 0) - (b[sortKey] || 0);
      return sortAsc ? diff : -diff;
    });
  }, [data, sortKey, sortAsc]);

  return (
    <CoachLayout title="Analytics">
      {/* Domain Selector */}
      <div style={{ marginBottom: 24 }}>
        <select
          value={domain}
          onChange={e => setDomain(e.target.value)}
          style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff', cursor: 'pointer' }}
        >
          <option value="">All Domains</option>
          {domains.map(d => (
            <option key={d.domain_id} value={d.domain_id}>{d.display_name || d.domain_id}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading analytics...</div>
      ) : !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Unable to load dashboard data.</div>
      ) : (
        <>
          {/* Top Stat Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 32 }}>
            <StatCard label="Total Learners" value={data.learner_count} color="#4f46e5" />
            <StatCard label="Active (7d)" value={data.active_last_7d} color="#10b981" />
            <StatCard label="Pending Reviews" value={data.submissions_pending_review} color="#f59e0b" sub="submissions" />
            <StatCard label="Forum Posts" value={data.forum_summary?.total_posts || 0} color="#6366f1" sub={`${data.forum_summary?.posts_this_week || 0} this week`} />
          </div>

          {/* Gauges Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Completion & Mastery</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Gauge label="Avg Completion" value={data.avg_completion_pct} max={100} color="#4f46e5" />
                <Gauge label="Avg Mastery" value={data.avg_mastery} max={1} color="#10b981" />
                <Gauge label="Code Lab Pass Rate" value={data.code_lab_summary?.avg_pass_rate || 0} max={1} color="#f59e0b" />
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Capstone Pipeline</h3>
              <CapstoneFunnel summary={data.capstone_summary} />
            </div>
          </div>

          {/* Code Lab + Forum Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12 }}>Code Lab Insights</h3>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 2 }}>
                <div><strong>Most Struggled Lab:</strong> {data.code_lab_summary?.most_struggled_lab || 'N/A'}</div>
                <div><strong>Avg Attempts per Lab:</strong> {data.code_lab_summary?.avg_attempts || 0}</div>
                <div><strong>Pass Rate:</strong> {Math.round((data.code_lab_summary?.avg_pass_rate || 0) * 100)}%</div>
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12 }}>Forum Health</h3>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 2 }}>
                <div><strong>Total Posts:</strong> {data.forum_summary?.total_posts || 0}</div>
                <div><strong>Posts This Week:</strong> {data.forum_summary?.posts_this_week || 0}</div>
                <div>
                  <strong>Pending Moderation:</strong>{' '}
                  <span style={{ color: (data.forum_summary?.pending_moderation || 0) > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                    {data.forum_summary?.pending_moderation || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Learner Leaderboard */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 32 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Learner Leaderboard</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>#</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Learner</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => handleSort('completion_pct')}>
                      Completion {sortKey === 'completion_pct' ? (sortAsc ? '↑' : '↓') : ''}
                    </th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => handleSort('mastery')}>
                      Mastery {sortKey === 'mastery' ? (sortAsc ? '↑' : '↓') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLeaderboard.map((l, i) => (
                    <tr key={l.learner_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', color: '#9ca3af' }}>{i + 1}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 500, color: '#111827' }}>{l.display_name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#4f46e5', fontWeight: 600 }}>{l.completion_pct}%</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{Math.round(l.mastery * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Content Effectiveness */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 16 }}>Content Effectiveness (Lowest Mastery)</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Module</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Domain</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Avg Mastery</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Completion Rate</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Learners</th>
                  </tr>
                </thead>
                <tbody>
                  {(effectiveness?.content_effectiveness || []).map((item, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500, color: '#111827' }}>{item.module_id}</td>
                      <td style={{ padding: '8px 12px', color: '#6b7280' }}>{item.domain}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: item.avg_mastery < 0.5 ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>
                        {Math.round(item.avg_mastery * 100)}%
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#4f46e5' }}>
                        {Math.round(item.completion_rate * 100)}%
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>{item.learner_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </CoachLayout>
  );
}
